import { sortAsc } from '../../utils/sortOptions';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  PlusIcon, XMarkIcon, MagnifyingGlassIcon,
  PencilIcon, BanknotesIcon,
} from '@heroicons/react/24/outline';

const fmt      = n => `Rs. ${(n||0).toLocaleString('en-PK')}`;
const fmtDate  = d => d ? new Date(d).toLocaleDateString('en-PK',{day:'numeric',month:'short',year:'numeric'}) : '—';
const daysLeft = d => d ? Math.max(0, Math.ceil((new Date(d)-new Date())/86400000)) : null;

const PLANS = ['free_trial','basic','pro','enterprise','custom','suspended'];
const PLAN_COLORS = { free_trial:'#f59e0b', basic:'#3b6ef8', pro:'#10c98e', enterprise:'#8b5cf6', custom:'#0d9488', suspended:'#ef4444' };
const PLAN_BG    = { free_trial:'#fffbeb', basic:'#eef2ff', pro:'#ecfdf5', enterprise:'#f3f0ff', custom:'#f0fdfa', suspended:'#fef2f2' };
const STATUS_COLORS = { active:'#10c98e', expired:'#ef4444', suspended:'#f59e0b', cancelled:'#9094a8' };

const PLAN_PRESETS = {
  free_trial: { monthlyPrice:0,    yearlyPrice:0,     trialDays:14, maxStudents:50,  maxTeachers:5,  maxClasses:3  },
  basic:      { monthlyPrice:2999, yearlyPrice:29999, trialDays:0,  maxStudents:200, maxTeachers:20, maxClasses:10 },
  pro:        { monthlyPrice:5999, yearlyPrice:59999, trialDays:0,  maxStudents:500, maxTeachers:50, maxClasses:25 },
  enterprise: { monthlyPrice:9999, yearlyPrice:99999, trialDays:0,  maxStudents:0,   maxTeachers:0,  maxClasses:0  },
  custom:     { monthlyPrice:0,    yearlyPrice:0,     trialDays:0,  maxStudents:0,   maxTeachers:0,  maxClasses:0  },
  suspended:  { monthlyPrice:0,    yearlyPrice:0,     trialDays:0,  maxStudents:0,   maxTeachers:0,  maxClasses:0  },
};

const initials = n => n?.split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase()||'S';

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .subs * { box-sizing:border-box; }
  .subs {
    font-family:'Sora',sans-serif; color:#1a1d2e;
    animation:ssFade .4s ease both;
    width:100%; min-width:0;
    padding:28px 32px;
    background:#f4f6fb;
    min-height:100vh;
  }
  @keyframes ssFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:none} }
  .subs-hdr { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .subs-title { font-size:1.35rem; font-weight:700; color:#0f1123; letter-spacing:-.02em; }
  .subs-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .stat-grid { display:grid; grid-template-columns:repeat(5,1fr); gap:1rem; margin-bottom:1.5rem; }
  .stat-card { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:1rem 1.1rem; }
  .stat-label { font-size:.67rem; font-weight:600; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .stat-val   { font-size:1.4rem; font-weight:700; font-family:'JetBrains Mono',monospace; margin-top:2px; }
  .subs-toolbar { display:flex; gap:.7rem; flex-wrap:wrap; margin-bottom:1.2rem; align-items:center; }
  .subs-search-wrap { position:relative; flex:1; min-width:200px; max-width:340px; }
  .subs-search-wrap svg { position:absolute; left:12px; top:50%; transform:translateY(-50%); width:15px; height:15px; stroke:#9094a8; pointer-events:none; }
  .subs-search { font-family:'Sora',sans-serif; font-size:.8rem; width:100%; padding:8px 12px 8px 34px; border:1.5px solid #e4e7f0; border-radius:10px; background:white; color:#1a1d2e; outline:none; }
  .subs-search:focus { border-color:#3b6ef8; }
  .fsel { font-family:'Sora',sans-serif; font-size:.78rem; padding:8px 12px; border:1.5px solid #e0e4f0; border-radius:10px; background:white; color:#1a1d2e; outline:none; }
  .btn-primary { display:inline-flex; align-items:center; gap:7px; padding:9px 18px; border:none; border-radius:10px; background:#3b6ef8; color:white; font-family:'Sora',sans-serif; font-size:.82rem; font-weight:600; cursor:pointer; }
  .btn-primary:hover { background:#2d5ef0; }
  .btn-primary svg { width:15px; height:15px; stroke-width:2; }
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; width:100%; }
  .tbl { width:100%; border-collapse:collapse; }
  .tbl thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .tbl th { padding:10px 16px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .tbl tbody tr { border-bottom:1px solid #f3f5fb; transition:background .12s; }
  .tbl tbody tr:last-child { border-bottom:none; }
  .tbl tbody tr:hover { background:#fafbff; }
  .tbl td { padding:11px 16px; font-size:.8rem; vertical-align:middle; text-align:center !important; }
  .school-avatar { width:34px; height:34px; border-radius:9px; background:linear-gradient(135deg,#3b6ef8,#8b5cf6); display:flex; align-items:center; justify-content:center; font-size:.74rem; font-weight:700; color:white; flex-shrink:0; overflow:hidden; }
  .school-avatar img { width:100%; height:100%; object-fit:cover; }
  .plan-pill { font-size:.67rem; font-weight:700; padding:3px 10px; border-radius:99px; display:inline-flex; }
  .status-dot { width:7px; height:7px; border-radius:50%; display:inline-block; margin-right:5px; }
  .action-btn { width:28px; height:28px; border-radius:7px; border:1px solid #e4e7f0; background:white; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:all .15s; }
  .action-btn svg { width:13px; height:13px; stroke-width:2; }
  .action-btn:hover { background:#eef2ff; border-color:#3b6ef8; }
  .days-badge { font-size:.67rem; font-weight:600; padding:2px 8px; border-radius:99px; display:inline-block; margin-top:2px; }
  .empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spin { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#3b6ef8; border-radius:50%; animation:sp .7s linear infinite; }
  @keyframes sp { to{transform:rotate(360deg)} }
  .mb { position:fixed; inset:0; background:rgba(10,12,28,.65); backdrop-filter:blur(5px); display:flex; align-items:center; justify-content:center; z-index:9999; padding:20px; }
  .mo { background:white; border-radius:20px; width:100%; max-width:580px; max-height:90vh; display:flex; flex-direction:column; box-shadow:0 25px 60px rgba(0,0,0,.25); animation:moIn .25s ease; overflow:hidden; }
  @keyframes moIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .mo-hd { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f2f8; flex-shrink:0; }
  .mo-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .mo-x { width:28px; height:28px; border-radius:7px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; }
  .mo-x svg { width:14px; height:14px; stroke-width:2; }
  .mo-bd { padding:20px 22px; flex:1; overflow-y:auto; }
  .mo-ft { display:flex; gap:8px; justify-content:flex-end; padding:14px 22px; border-top:1px solid #f0f2f8; flex-shrink:0; }
  .fg { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
  .form-grid > *, .fg > * { min-width:0; }
  .ff { display:flex; flex-direction:column; gap:5px; }
  .ff.full { grid-column:1/-1; }
  .fl { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .fi,.fs { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%;min-width:0; }
  .fi:focus,.fs:focus { border-color:#3b6ef8; background:white; }
  .fta { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%;min-width:0; resize:vertical; min-height:60px; }
  .fta:focus { border-color:#3b6ef8; background:white; }
  .btn-save  { display:inline-flex; align-items:center; gap:7px; padding:9px 18px; border:none; border-radius:10px; background:#3b6ef8; color:white; font-family:'Sora',sans-serif; font-size:.82rem; font-weight:600; cursor:pointer; }
  .btn-save:disabled { opacity:.5; cursor:not-allowed; }
  .btn-ghost { display:inline-flex; align-items:center; gap:7px; padding:9px 18px; border:none; border-radius:10px; background:#f0f2f7; color:#6b7194; font-family:'Sora',sans-serif; font-size:.82rem; font-weight:600; cursor:pointer; }
  .plan-selector { display:grid; grid-template-columns:repeat(3,1fr); gap:8px; margin-bottom:14px; }
  .plan-opt { padding:10px; border-radius:10px; border:2px solid #e8ebf4; cursor:pointer; text-align:center; transition:all .15s; }
  .plan-opt.sel { border-color:var(--pc); background:var(--pb); }
  .plan-opt-name  { font-size:.78rem; font-weight:700; }
  .plan-opt-price { font-size:.65rem; color:#9094a8; margin-top:2px; font-family:'JetBrains Mono',monospace; }

  /* Mobile cards */
  .subs-cards { display:none; padding:10px; }
  .subs-card { background:white; border-radius:12px; border:1px solid #e8ebf4; padding:13px; margin-bottom:8px; }
  .subs-card-top { display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom:8px; }
  .subs-card-row { display:flex; justify-content:space-between; align-items:center; font-size:.78rem; padding:5px 0; border-bottom:1px solid #f3f5fb; gap:8px; }
  .subs-card-row:last-of-type { border-bottom:none; }
  .subs-card-label { color:#9094a8; font-weight:600; font-size:.68rem; text-transform:uppercase; letter-spacing:.05em; flex-shrink:0; }
  .subs-card-actions { display:flex; gap:6px; margin-top:10px; }
  @media (max-width:900px) { .stat-grid { grid-template-columns:repeat(3,1fr); } }
  @media (max-width:768px) {
    .subs-tbl-wrap { display:none !important; }
    .subs-cards { display:block; }
  }
  @media (max-width:600px) { .subs { padding:16px; } .stat-grid { grid-template-columns:repeat(2,1fr); } .fg { grid-template-columns:1fr; } .ff.full { grid-column:1; } .plan-selector { grid-template-columns:repeat(2,1fr); } }
`;

export default function SuperSubscriptions() {
  const [subs,     setSubs]    = useState([]);
  const [stats,    setStats]   = useState({});
  const [schools,  setSchools] = useState([]);
  const [loading,  setLoading] = useState(true);
  const [search,   setSearch]  = useState('');
  const [planF,    setPlanF]   = useState('');
  const [modal,    setModal]   = useState(null);
  const [payModal, setPayModal]= useState(null);
  const [saving,   setSaving]  = useState(false);
  const [form,     setForm]    = useState({});
  const [payForm,  setPayForm] = useState({ amount:'', method:'cash', reference:'', note:'' });

  const load = () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (search) p.append('search', search);
    if (planF)  p.append('plan',   planF);
    api.get(`/super/subscriptions?${p}`)
      .then(r => { setSubs(r.data.subscriptions||[]); setStats(r.data.stats||{}); })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [search, planF]);

  useEffect(() => {
    api.get('/super/schools').then(r => setSchools(r.data?.schools||r.data||[])).catch(()=>{});
  }, []);

  const openCreate = () => {
    setForm({ plan:'free_trial', status:'active', billingCycle:'monthly', ...PLAN_PRESETS.free_trial });
    setModal('create');
  };

  const openEdit = (sub) => {
    setForm({
      schoolId:     sub.school?._id,
      plan:         sub.plan,
      status:       sub.status,
      billingCycle: sub.billingCycle,
      monthlyPrice: sub.monthlyPrice,
      yearlyPrice:  sub.yearlyPrice,
      trialDays:    sub.trialDays,
      endDate:      sub.endDate ? new Date(sub.endDate).toISOString().slice(0,10) : '',
      maxStudents:  sub.maxStudents,
      maxTeachers:  sub.maxTeachers,
      maxClasses:   sub.maxClasses,
      notes:        sub.notes||'',
    });
    setModal(sub);
  };

  const setPlan = (plan) => setForm(p => ({ ...p, plan, ...PLAN_PRESETS[plan]||{} }));
  const setF    = (k,v)  => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (modal === 'create' && !form.schoolId) return toast.error('Select a school');
    setSaving(true);
    try {
      const payload = { ...form, schoolId: form.schoolId || modal?.school?._id };
      if (modal === 'create') await api.post('/super/subscriptions', payload);
      else                    await api.put(`/super/subscriptions/${modal._id}`, payload);
      toast.success('Subscription saved');
      setModal(null);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const handlePayment = async () => {
    if (!payForm.amount) return toast.error('Amount required');
    setSaving(true);
    try {
      await api.post(`/super/subscriptions/${payModal._id}/payment`, payForm);
      toast.success('Payment recorded');
      setPayModal(null);
      load();
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="subs"><style>{css}</style>

      <div className="subs-hdr">
        <div>
          <div className="subs-title">Subscriptions</div>
          <div className="subs-sub">Manage school plans, free trials and billing</div>
        </div>
        <button className="btn-primary" onClick={openCreate}><PlusIcon />New Subscription</button>
      </div>

      {/* Stats */}
      <div className="stat-grid">
        {[
          { label:'Total',       val: stats.total||0,          color:'#3b6ef8' },
          { label:'Active',      val: stats.active||0,         color:'#10c98e' },
          { label:'Trial',       val: stats.trial||0,          color:'#f59e0b' },
          { label:'Expired',     val: stats.expired||0,        color:'#ef4444' },
          { label:'Monthly Rev', val: fmt(stats.monthlyRevenue||0), color:'#8b5cf6', sm:true },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-label">{s.label}</div>
            <div className="stat-val" style={{color:s.color,fontSize:s.sm?'1rem':'1.4rem'}}>{s.val}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="subs-toolbar">
        <div className="subs-search-wrap">
          <MagnifyingGlassIcon />
          <input className="subs-search" placeholder="Search school…" value={search} onChange={e=>setSearch(e.target.value)} />
        </div>
        <select className="fsel" value={planF} onChange={e=>setPlanF(e.target.value)}>
          <option value="">All Plans</option>
          {sortAsc(PLANS, p=>p.replace('_',' ')).map(p => <option key={p} value={p}>{p.replace('_',' ')}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="panel">
        {loading ? (
          <div className="loading"><div className="spin"/>Loading…</div>
        ) : subs.length === 0 ? (
          <div className="empty">No subscriptions found.</div>
        ) : (
          <>
          <div className="subs-tbl-wrap" style={{overflowX:'auto'}}>
            <table className="tbl">
              <thead><tr>
                <th>School</th><th>Plan</th><th>Status</th><th>Billing</th>
                <th>Price</th><th>Expires</th><th>Limits</th><th>Actions</th>
              </tr></thead>
              <tbody>
                {subs.map(sub => {
                  const dl = daysLeft(sub.endDate);
                  return (
                    <tr key={sub._id}>
                      <td>
                        <div style={{display:'flex',alignItems:'center',gap:9}}>
                          <div className="school-avatar">
                            {sub.school?.logo ? <img src={sub.school.logo} alt=""/> : initials(sub.school?.name)}
                          </div>
                          <div>
                            <div style={{fontWeight:600,color:'#0f1123'}}>{sub.school?.name||'—'}</div>
                            <div style={{fontSize:'.68rem',color:'#9094a8'}}>{sub.school?.email||''}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="plan-pill" style={{background:PLAN_BG[sub.plan],color:PLAN_COLORS[sub.plan]}}>
                          {sub.plan?.replace('_',' ')}
                        </span>
                      </td>
                      <td>
                        <span style={{display:'inline-flex',alignItems:'center',fontSize:'.76rem',fontWeight:600,color:STATUS_COLORS[sub.status]||'#9094a8'}}>
                          <span className="status-dot" style={{background:STATUS_COLORS[sub.status]||'#9094a8'}}/>
                          {sub.status}
                        </span>
                      </td>
                      <td style={{fontSize:'.76rem',color:'#6b7194',textTransform:'capitalize'}}>{sub.billingCycle}</td>
                      <td style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.76rem',fontWeight:600}}>
                        {sub.plan==='free_trial' ? 'Free' : fmt(sub.billingCycle==='yearly' ? sub.yearlyPrice : sub.monthlyPrice)}
                      </td>
                      <td>
                        <div style={{fontSize:'.74rem',color:'#6b7194'}}>{fmtDate(sub.endDate)}</div>
                        {dl !== null && (
                          <span className="days-badge" style={{
                            background: dl<=3?'#fef2f2':dl<=7?'#fffbeb':'#ecfdf5',
                            color:      dl<=3?'#ef4444':dl<=7?'#f59e0b':'#10c98e',
                          }}>{dl}d left</span>
                        )}
                      </td>
                      <td style={{fontSize:'.72rem',color:'#9094a8'}}>
                        {sub.maxStudents>0?`${sub.maxStudents}s`:'∞s'} · {sub.maxTeachers>0?`${sub.maxTeachers}t`:'∞t'}
                      </td>
                      <td>
                        <div style={{display:'flex',gap:5}}>
                          <button className="action-btn" onClick={()=>openEdit(sub)} title="Edit"><PencilIcon /></button>
                          <button className="action-btn" title="Record Payment"
                            style={{color:'#10c98e',borderColor:'#a7f3d0'}}
                            onClick={()=>{setPayModal(sub);setPayForm({amount:'',method:'cash',reference:'',note:''});}}>
                            <BanknotesIcon />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="subs-cards">
            {subs.map(sub => {
              const dl = daysLeft(sub.endDate);
              return (
                <div className="subs-card" key={sub._id}>
                  <div className="subs-card-top">
                    <div style={{display:'flex',alignItems:'center',gap:9}}>
                      <div className="school-avatar">
                        {sub.school?.logo ? <img src={sub.school.logo} alt=""/> : initials(sub.school?.name)}
                      </div>
                      <div>
                        <div style={{fontWeight:600,color:'#0f1123'}}>{sub.school?.name||'—'}</div>
                        <div style={{fontSize:'.68rem',color:'#9094a8'}}>{sub.school?.email||''}</div>
                      </div>
                    </div>
                    <span className="plan-pill" style={{background:PLAN_BG[sub.plan],color:PLAN_COLORS[sub.plan]}}>
                      {sub.plan?.replace('_',' ')}
                    </span>
                  </div>
                  <div className="subs-card-row"><span className="subs-card-label">Status</span>
                    <span style={{display:'inline-flex',alignItems:'center',fontSize:'.76rem',fontWeight:600,color:STATUS_COLORS[sub.status]||'#9094a8'}}>
                      <span className="status-dot" style={{background:STATUS_COLORS[sub.status]||'#9094a8'}}/>
                      {sub.status}
                    </span>
                  </div>
                  <div className="subs-card-row"><span className="subs-card-label">Billing</span><span style={{fontSize:'.76rem',color:'#6b7194',textTransform:'capitalize'}}>{sub.billingCycle}</span></div>
                  <div className="subs-card-row"><span className="subs-card-label">Price</span>
                    <span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.76rem',fontWeight:600}}>
                      {sub.plan==='free_trial' ? 'Free' : fmt(sub.billingCycle==='yearly' ? sub.yearlyPrice : sub.monthlyPrice)}
                    </span>
                  </div>
                  <div className="subs-card-row"><span className="subs-card-label">Expires</span>
                    <span style={{textAlign:'right'}}>
                      <div style={{fontSize:'.74rem',color:'#6b7194'}}>{fmtDate(sub.endDate)}</div>
                      {dl !== null && (
                        <span className="days-badge" style={{
                          background: dl<=3?'#fef2f2':dl<=7?'#fffbeb':'#ecfdf5',
                          color:      dl<=3?'#ef4444':dl<=7?'#f59e0b':'#10c98e',
                        }}>{dl}d left</span>
                      )}
                    </span>
                  </div>
                  <div className="subs-card-row"><span className="subs-card-label">Limits</span>
                    <span style={{fontSize:'.72rem',color:'#9094a8'}}>
                      {sub.maxStudents>0?`${sub.maxStudents}s`:'∞s'} · {sub.maxTeachers>0?`${sub.maxTeachers}t`:'∞t'}
                    </span>
                  </div>
                  <div className="subs-card-actions">
                    <button className="action-btn" onClick={()=>openEdit(sub)} title="Edit"><PencilIcon /></button>
                    <button className="action-btn" title="Record Payment"
                      style={{color:'#10c98e',borderColor:'#a7f3d0'}}
                      onClick={()=>{setPayModal(sub);setPayForm({amount:'',method:'cash',reference:'',note:''});}}>
                      <BanknotesIcon />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          </>
        )}
      </div>

      {/* Create / Edit Modal */}
      {modal !== null && createPortal(
        <div className="mb" onClick={()=>setModal(null)}>
          <div className="mo" onClick={e=>e.stopPropagation()}>
            <div className="mo-hd">
              <div className="mo-title">{modal==='create' ? 'New Subscription' : 'Edit Subscription'}</div>
              <button className="mo-x" onClick={()=>setModal(null)}><XMarkIcon /></button>
            </div>
            <div className="mo-bd">
              {modal === 'create' && (
                <div className="ff full" style={{marginBottom:16}}>
                  <label className="fl">School *</label>
                  <select className="fs" value={form.schoolId||''} onChange={e=>setF('schoolId',e.target.value)}>
                    <option value="">Select school…</option>
                    {schools.map(s=><option key={s._id} value={s._id}>{s.name}</option>)}
                  </select>
                </div>
              )}
              <div className="fl" style={{marginBottom:8}}>Plan</div>
              <div className="plan-selector">
                {PLANS.filter(p=>p!=='suspended').map(p => (
                  <div key={p} className={`plan-opt${form.plan===p?' sel':''}`}
                    style={{'--pc':PLAN_COLORS[p],'--pb':PLAN_BG[p]}}
                    onClick={()=>setPlan(p)}>
                    <div className="plan-opt-name" style={{color:form.plan===p?PLAN_COLORS[p]:'#1a1d2e'}}>{p.replace('_',' ')}</div>
                    <div className="plan-opt-price">{p==='free_trial'?'Free':fmt(PLAN_PRESETS[p]?.monthlyPrice)+'/mo'}</div>
                  </div>
                ))}
              </div>
              <div className="fg">
                <div className="ff">
                  <label className="fl">Status</label>
                  <select className="fs" value={form.status||'active'} onChange={e=>setF('status',e.target.value)}>
                    {['active','expired','suspended','cancelled'].map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="ff">
                  <label className="fl">Billing Cycle</label>
                  <select className="fs" value={form.billingCycle||'monthly'} onChange={e=>setF('billingCycle',e.target.value)}>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
                {form.plan==='free_trial' ? (
                  <div className="ff">
                    <label className="fl">Trial Days</label>
                    <input type="number" className="fi" value={form.trialDays||14} onChange={e=>setF('trialDays',e.target.value)} />
                  </div>
                ) : (
                  <div className="ff">
                    <label className="fl">Monthly Price (Rs.)</label>
                    <input type="number" className="fi" value={form.monthlyPrice||0} onChange={e=>setF('monthlyPrice',e.target.value)} />
                  </div>
                )}
                {form.plan !== 'free_trial' && (
                  <div className="ff">
                    <label className="fl">Yearly Price (Rs.)</label>
                    <input type="number" className="fi" value={form.yearlyPrice||0} onChange={e=>setF('yearlyPrice',e.target.value)} />
                  </div>
                )}
                <div className="ff">
                  <label className="fl">End Date</label>
                  <input type="date" className="fi" value={form.endDate||''} onChange={e=>setF('endDate',e.target.value)} />
                </div>
                <div className="ff"><label className="fl">Max Students (0=∞)</label><input type="number" className="fi" value={form.maxStudents||0} onChange={e=>setF('maxStudents',e.target.value)} /></div>
                <div className="ff"><label className="fl">Max Teachers (0=∞)</label><input type="number" className="fi" value={form.maxTeachers||0} onChange={e=>setF('maxTeachers',e.target.value)} /></div>
                <div className="ff"><label className="fl">Max Classes (0=∞)</label><input type="number" className="fi" value={form.maxClasses||0} onChange={e=>setF('maxClasses',e.target.value)} /></div>
                <div className="ff full">
                  <label className="fl">Notes</label>
                  <textarea className="fta" value={form.notes||''} onChange={e=>setF('notes',e.target.value)} placeholder="Internal notes…"/>
                </div>
              </div>
            </div>
            <div className="mo-ft">
              <button className="btn-ghost" onClick={()=>setModal(null)}>Cancel</button>
              <button className="btn-save" onClick={handleSave} disabled={saving}>{saving?'Saving…':'Save Subscription'}</button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Payment Modal */}
      {payModal && createPortal(
        <div className="mb" onClick={()=>setPayModal(null)}>
          <div className="mo" style={{maxWidth:420}} onClick={e=>e.stopPropagation()}>
            <div className="mo-hd">
              <div className="mo-title">Record Payment — {payModal.school?.name}</div>
              <button className="mo-x" onClick={()=>setPayModal(null)}><XMarkIcon /></button>
            </div>
            <div className="mo-bd">
              <div className="fg">
                <div className="ff full">
                  <label className="fl">Amount (Rs.) *</label>
                  <input type="number" className="fi" value={payForm.amount} onChange={e=>setPayForm(p=>({...p,amount:e.target.value}))} placeholder="e.g. 2999" />
                </div>
                <div className="ff">
                  <label className="fl">Method</label>
                  <select className="fs" value={payForm.method} onChange={e=>setPayForm(p=>({...p,method:e.target.value}))}>
                    {['cash','bank','online','cheque'].map(m=><option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div className="ff">
                  <label className="fl">Reference #</label>
                  <input className="fi" value={payForm.reference} onChange={e=>setPayForm(p=>({...p,reference:e.target.value}))} placeholder="Transaction ID" />
                </div>
                <div className="ff full">
                  <label className="fl">Note</label>
                  <textarea className="fta" value={payForm.note} onChange={e=>setPayForm(p=>({...p,note:e.target.value}))} placeholder="Payment note…"/>
                </div>
              </div>
            </div>
            <div className="mo-ft">
              <button className="btn-ghost" onClick={()=>setPayModal(null)}>Cancel</button>
              <button className="btn-save" style={{background:'#10c98e'}} onClick={handlePayment} disabled={saving}>
                {saving?'Recording…':'Record Payment'}
              </button>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
}